import { makeCurrencyFormatter, percentageFormatter, scaledPercentageFormatter } from "helpers/formatters/numbers"
import { get, isNil, keyBy, merge, map, isEmpty, chain, toLower } from "lodash"
import { GMV, REFERENCE_EQUITY, MODEL_COVERAGE, POSITIONS, MAX_CONCENTRATION, MIN_CONCENTRATION, LONG, SHORT, OVERWEIGHT, UNDERWEIGHT, NET, MARKET_CAP } from "../constants"
import { getMetricValue } from "../composition-helpers"
import { sanitizePositionSet } from "../helpers"

export const getMetrics = (currency) => {
  const currencyFormatter = makeCurrencyFormatter(currency)
  return {
    [GMV]: {
      label: "GMV",
      path: "gmv",
      formatter: currencyFormatter,
    },
    [REFERENCE_EQUITY]: {
      label: "Reference Equity",
      path: "referenceEquity",
      formatter: currencyFormatter,
    },
    [MODEL_COVERAGE]: {
      label: "Model Coverage",
      path: "coverage.percentGmvAvailable",
      formatter: percentageFormatter,
    },
    [POSITIONS]: {
      label: "Positions",
      path: "positionsCount",
      // Generally there will be a default formatter applied in the renderer but in this case, we specifically want to show the raw number
      // so we have to override it here
      formatter: null,
    },
    [MAX_CONCENTRATION]: {
      label: "Max Concentration",
      path: "concentration.max",
    },
    [MIN_CONCENTRATION]: {
      label: "Min Concentration",
      path: "concentration.min",
    },
    [MARKET_CAP]: {
      label: "Market Capitalization",
      path: "summaryStats.average.marketCapitalization",
      formatter: currencyFormatter,
    },
  }
}

function makeLongShortGetter(metricId) {
  return (item, defaultZero) => {
    const value = chain(item)
      .find(({ id }) => toLower(id) === metricId)
      .get("economicExposure", defaultZero ? 0 : null)
      .value()

    if (isNil(value)) {
      return value
    }
    return Math.abs(value)
  }
}

export const getLongShortMetrics = (isActive) => {
  const metrics = {
    [NET]: {
      label: "Net",
      path: "net",
      formatter: scaledPercentageFormatter,
      valueGetter: (compositionBy, defaultZero) => {
        const long = makeLongShortGetter(isActive ? OVERWEIGHT : LONG)(compositionBy, defaultZero)
        const short = makeLongShortGetter(isActive ? UNDERWEIGHT : SHORT)(compositionBy, defaultZero)
        return long - Math.abs(short)
      },
    },
  }
  if (isActive) {
    metrics[OVERWEIGHT] = {
      label: "Overweight",
      path: "overweight",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(OVERWEIGHT),
    }
    metrics[UNDERWEIGHT] = {
      label: "Underweight",
      path: "underweight",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(UNDERWEIGHT),
    }
  } else {
    metrics[LONG] = {
      label: "Long",
      path: "long",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(LONG),
    }
    metrics[SHORT] = {
      label: "Short",
      path: "short",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(SHORT),
    }
  }
  return metrics
}

const QUERY = `query compareCompositionPositionSet($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $groupBy: ContributorGroupType!) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      coverage {
        date
        coverage: summary  {
          percentGmvAvailable
        }
      }
      longShort: composition(scaleFormat: PERCENT_EQUITY_GMV) {
        date
        compositionBy(groupBy: $groupBy) {
          id
          description
          economicExposure
        }
      }
      composition(scaleFormat: PERCENT_EQUITY_GMV) {
        date
        gmv
        referenceEquity
        positionsCount
        modeledGmv
        summaryStats {
          average {
            marketCapitalization
          }
        }
        concentration {
          min
          max
        }
      }
    }
  }
}`

export function createCompositionMultiDateQuery(modelId, positionSet, base, from, to, options = {}) {
  const isActive = !isEmpty(base)
  const defaultZeros = options?.defaultZeros || false
  const noGmvOrEquity = options?.noGmvOrEquity || false
  const hasReferenceEquity = options?.hasReferenceEquity || false
  const currency = options?.currency || "USD"

  const longShortMetrics = getLongShortMetrics(isActive)
  const allMetrics = {
    ...getMetrics(currency),
    ...longShortMetrics,
  }

  return {
    key: ["composition", "multiDate", modelId, positionSet, base, from, to],
    query: QUERY,
    variables: {
      modelId,
      positionSet: {
        ...sanitizePositionSet(positionSet),
        currency,
      },
      base,
      from,
      to,
      groupBy: isActive ? "OVERWEIGHT_UNDERWEIGHT" : "LONG_SHORT",
    },
    select: (graphqlResult) => {
      const coverage = keyBy(get(graphqlResult, "data.model.simulation.coverage", []), "date")
      const beta = keyBy(get(graphqlResult, "data.model.simulation.beta", []), "date")
      const composition = keyBy(get(graphqlResult, "data.model.simulation.composition", []), "date")

      // We want to simplify the longShort data structure to be { [long]: { date: value }, [short]: { date: value }, [net]: { date: value } }
      const longShort = chain(graphqlResult)
        .get("data.model.simulation.longShort", [])
        .keyBy("date")
        .mapValues(({ compositionBy }) =>
          Object.entries(longShortMetrics).reduce((acc, [metricId, { valueGetter }]) => {
            acc[metricId] = valueGetter(compositionBy, defaultZeros)
            return acc
          }, {}),
        )
        .value()

      const all = merge({}, coverage, composition, beta, longShort)

      return {
        values: {
          dates: map(all, (data, date) => {
            const metrics = Object.entries(allMetrics).reduce((acc, [metricId]) => {
              acc[metricId] = getMetricValue(metricId, allMetrics, data, noGmvOrEquity, hasReferenceEquity)
              return acc
            }, {})
            return {
              date,
              ...metrics,
            }
          }),
        },
      }
    },
  }
}
